/**
 * ExplorePageV2 — Phase 4: Explore Literature V2
 * Behind ENABLE_EXPLORE_TOPIC_SEARCH_V2
 *
 * Features: typeahead search, study-type chips, lookback selector,
 *           "Use my preferences" toggle, bucketed results.
 * PHI-Zero: query text is never logged here.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import ArticleCard from '../components/ArticleCard';
import { articlesAPI, preferencesAPI } from '../api';
import { toast } from 'sonner';
import { Search, ArrowLeft, X, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react';

// ── Constants ──────────────────────────────────────────────────────────────

const STUDY_TYPES = [
  { id: 'systematic_review', label: 'Systematic review / Meta-analysis' },
  { id: 'guidelines',        label: 'Guidelines' },
  { id: 'review',            label: 'Review articles' },
  { id: 'rct',               label: 'RCTs' },
  { id: 'observational',     label: 'Observational / Cohort' },
];

const LOOKBACK_OPTIONS = [
  { value: 0,  label: 'All time' },
  { value: 1,  label: '1 year' },
  { value: 3,  label: '3 years' },
  { value: 5,  label: '5 years' },
  { value: 10, label: '10 years' },
];

// ── Typeahead input ────────────────────────────────────────────────────────

const TypeaheadInput = ({ value, onChange, onSearch, loading, placeholder }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const data = await articlesAPI.topicSuggest(q);
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 250);
    setShowSuggestions(true);
  };

  const handleSelect = (sug) => {
    onChange(sug);
    setSuggestions([]);
    setShowSuggestions(false);
    onSearch(sug);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { setShowSuggestions(false); onSearch(value); }
    if (e.key === 'Escape') setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" aria-hidden="true" />
        <input
          type="text"
          className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm transition"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          aria-label="Search medical literature"
          aria-autocomplete="list"
          aria-expanded={showSuggestions && suggestions.length > 0}
          data-testid="explore-search-input"
          autoComplete="off"
        />
        {value && (
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 focus:outline-none"
            onClick={() => { onChange(''); setSuggestions([]); }}
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
          role="listbox"
          aria-label="Search suggestions"
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              role="option"
              className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer flex items-center gap-2 transition-colors"
              onMouseDown={() => handleSelect(s)}
            >
              <Search className="h-3 w-3 text-slate-400 shrink-0" aria-hidden="true" />
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Study type chip ────────────────────────────────────────────────────────

const StudyChip = ({ id, label, selected, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500
      ${selected
        ? 'bg-sky-600 border-sky-600 text-white shadow-sm'
        : 'bg-white border-slate-200 text-slate-600 hover:border-sky-400 hover:text-sky-700'}`}
    aria-pressed={selected}
    data-testid={`study-chip-${id}`}
  >
    {label}
  </button>
);

// ── Lookback button ────────────────────────────────────────────────────────

const LookbackBtn = ({ value, label, selected, onClick }) => (
  <button
    onClick={() => onClick(value)}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500
      ${selected
        ? 'bg-slate-800 border-slate-800 text-white'
        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'}`}
    aria-pressed={selected}
    data-testid={`lookback-${value}`}
  >
    {label}
  </button>
);

// ── Main page component ────────────────────────────────────────────────────

const ExplorePageV2 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [query, setQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [lookback, setLookback] = useState(3);
  const [usePrefs, setUsePrefs] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // null = not searched yet
  const [searchContext, setSearchContext] = useState(null);
  const [hasPreferences, setHasPreferences] = useState(false);

  // Load preferences status (for toggle display)
  useEffect(() => {
    preferencesAPI.getMyPreferences().then(() => setHasPreferences(true)).catch(() => setHasPreferences(false));
  }, []);

  const toggleType = (id) => {
    setSelectedTypes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };

  const handleSearch = async (overrideQuery) => {
    const q = (overrideQuery ?? query).trim();
    if (!q) { toast.error('Enter a topic to search'); return; }

    setLoading(true);
    setResults(null);
    try {
      const resp = await articlesAPI.searchV2({
        query: q,
        use_preferences_context: usePrefs && hasPreferences,
        study_types: selectedTypes,
        lookback_years: lookback,
        limit: 30,
      });
      setResults(resp.articles || []);
      setSearchContext(resp.search_context || null);
      if ((resp.articles || []).length === 0) {
        toast.info('No results found. Try broadening your search or changing the lookback period.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error_code === 'feature_disabled') {
        toast.error('Explore V2 is not enabled.');
      } else {
        toast.error('Search failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setSelectedTypes([]);
    setLookback(3);
    setResults(null);
    setQuery('');
    setSearchContext(null);
  };

  const activeFilters = selectedTypes.length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="explore-v2-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <Button variant="ghost" size="sm" onClick={() => navigate('/home')} className="mb-3 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Explore Literature</h1>
          <p className="text-slate-500 text-sm mt-0.5">Search PubMed — results ordered by evidence strength</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {/* Search bar row */}
        <div className="flex gap-2 items-start">
          <TypeaheadInput
            value={query}
            onChange={setQuery}
            onSearch={handleSearch}
            loading={loading}
            placeholder="Search a topic (e.g. heart failure, GLP-1, CAR-T)"
          />
          <Button
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
            className="h-11 px-5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white shrink-0"
            data-testid="explore-search-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </div>

        {/* Controls row */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="explore-filters">

          {/* Study types */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Study type</span>
              {activeFilters > 0 && (
                <button
                  className="text-xs text-sky-600 hover:text-sky-700 focus:outline-none"
                  onClick={() => setSelectedTypes([])}
                  aria-label="Clear study type filters"
                >
                  Clear ({activeFilters})
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Study type filters">
              {STUDY_TYPES.map(t => (
                <StudyChip
                  key={t.id}
                  id={t.id}
                  label={t.label}
                  selected={selectedTypes.includes(t.id)}
                  onClick={toggleType}
                />
              ))}
            </div>
            {activeFilters === 0 && (
              <p className="text-[11px] text-slate-400 mt-2">
                No filter selected — results ordered by: systematic reviews → guidelines → review articles → RCTs → observational
              </p>
            )}
          </div>

          {/* Lookback + preferences row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 pt-2 border-t border-slate-100">
            <div>
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-2">Lookback</span>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Lookback period">
                {LOOKBACK_OPTIONS.map(o => (
                  <LookbackBtn
                    key={o.value}
                    value={o.value}
                    label={o.label}
                    selected={lookback === o.value}
                    onClick={setLookback}
                  />
                ))}
              </div>
            </div>

            {/* Preferences toggle */}
            <div className="sm:ml-auto">
              <button
                className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded"
                onClick={() => setUsePrefs(p => !p)}
                aria-pressed={usePrefs}
                data-testid="use-prefs-toggle"
                disabled={!hasPreferences}
                title={!hasPreferences ? 'No preferences configured' : undefined}
              >
                {usePrefs && hasPreferences
                  ? <ToggleRight className="h-5 w-5 text-sky-600" aria-hidden="true" />
                  : <ToggleLeft className="h-5 w-5 text-slate-300" aria-hidden="true" />}
                <span className={usePrefs && hasPreferences ? 'text-sky-700 font-medium' : 'text-slate-400'}>
                  Use my preferences
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Search context badge */}
        {searchContext && (
          <div className="flex flex-wrap gap-2 text-xs" aria-live="polite">
            {searchContext.preferences_applied && (
              <Badge variant="secondary" className="bg-sky-50 text-sky-700 border-sky-200" data-testid="context-prefs-badge">
                Preference context applied
              </Badge>
            )}
            {searchContext.bucket_mode && (
              <Badge variant="secondary" className="bg-slate-100 text-slate-600" data-testid="context-bucket-badge">
                Ordered by evidence strength
              </Badge>
            )}
            {searchContext.lookback_years > 0 && (
              <Badge variant="outline" className="text-slate-500">
                Last {searchContext.lookback_years} {searchContext.lookback_years === 1 ? 'year' : 'years'}
              </Badge>
            )}
          </div>
        )}

        {/* Results */}
        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="explore-loading">
            <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
            <span className="ml-3 text-slate-500 text-sm">Searching PubMed…</span>
          </div>
        )}

        {!loading && results !== null && results.length > 0 && (
          <div data-testid="explore-results">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-slate-700">
                {results.length} result{results.length !== 1 ? 's' : ''}
              </p>
              <button
                className="text-xs text-slate-400 hover:text-slate-600 focus:outline-none"
                onClick={clearAll}
              >
                Clear search
              </button>
            </div>
            <div className="space-y-3">
              {results.map(article => (
                <ArticleCard
                  key={article.pmid}
                  article={article}
                  showFeedback={true}
                  context="explore"
                />
              ))}
            </div>
          </div>
        )}

        {!loading && results !== null && results.length === 0 && (
          <Card data-testid="explore-empty">
            <CardContent className="py-14 text-center">
              <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="font-medium text-slate-700">No results found</p>
              <p className="text-sm text-slate-400 mt-1">
                Try broader terms, or change the lookback period or study types.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && results === null && (
          <Card data-testid="explore-idle">
            <CardContent className="py-14 text-center">
              <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="font-medium text-slate-700">Search any clinical topic</p>
              <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
                Results are ordered by evidence strength when no study type filter is applied.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExplorePageV2;
